import * as React from 'react';

import { LatLngBounds } from 'leaflet';
import Control from 'react-leaflet-control';
import { Map, TileLayer } from 'react-leaflet';

import { useTranslation } from '@lib/useTypedTranslation';
import { Trans } from '@components/i18n/typedTrans';
import { RequestInitWithRetry } from '@lib/request';
import { useDebounce } from '@lib/useDebounce';
import { UseRequestReturn } from '@lib/useRequest';

import { ClusterContext } from '../../../containers/core/maps/clusterContext';
import { DeviceClusterResponse, DeviceStatsResponse, getDeviceGeoBounds } from '../../../services/core/maps';
import { useWorldRequest } from '../../../lib/useWorldRequest';
import { NoSelectionOverlay } from '../../card/noSelectionOverlay';
import { getDataRequestBounds, useInitialBoundsSet, useZoom } from '../utils';
import { OverlayLayer } from './overlayLayer';

import '../sharedMap.css';

export interface ClusterMapProps {
  deviceStatsRequest: UseRequestReturn<DeviceStatsResponse>,
  fetchMapData: (boundingLatLong: string, geotilePrecision: number) => (options: RequestInitWithRetry) => Promise<DeviceClusterResponse>
}

export const ClusterMap = ({
  deviceStatsRequest,
  fetchMapData
}: ClusterMapProps) => {
  const mapRef = React.useRef<Map>();
  const { t } = useTranslation('translation');

  // INTERACTION STATE
  const { state, dispatch } = React.useContext(ClusterContext);
  const [bounds, setBounds] = React.useState<LatLngBounds>();
  const [geotilePrecision, setGeotilePrecision] = React.useState<number>();

  const { addDebounceEvent, isDebouncing } = useDebounce(500);

  const getFetch = React.useCallback(() => {
    const getMapBounds = getDeviceGeoBounds({ });
    return getMapBounds;
  }, []);

  const initialBoundsRequest = useWorldRequest(getFetch);

  const { resetInitialBounds, mapTilesShown } = useInitialBoundsSet(
    'cluster',
    initialBoundsRequest,
    mapRef,
  );

  // Map Data
  const fetchMapDataRequest = React.useCallback(() => {
    // no fetcher returned if no bounds or precision
    if (!bounds) {
      return undefined;
    }

    const boundsSw = bounds.getSouthWest();
    const boundsNe = bounds.getNorthEast();

    const mapBounds = getDataRequestBounds('cluster', boundsSw, boundsNe, geotilePrecision);
    const boundingLatLong = `${mapBounds.getSouth()},${mapBounds.getWest()}:${mapBounds.getNorth()},${mapBounds.getEast()}`;

    return fetchMapData(boundingLatLong, geotilePrecision);
  }, [bounds, geotilePrecision, fetchMapData]);

  const mapDataRequest = useWorldRequest(fetchMapDataRequest);

  // triggers fresh map data fetching via useWorldRequest
  const prepareMapDataRequest = React.useCallback(() => {
    if (initialBoundsRequest.data) {
      const bounds = mapRef.current?.leafletElement?.getBounds();
      const zoom = mapRef.current?.leafletElement?.getZoom() || 0;
      const geotilePrecision = zoom + 3;

      setBounds(bounds);
      setGeotilePrecision(geotilePrecision);
    }
  }, [initialBoundsRequest.data]);

  const handleChange = React.useCallback(() => {
    addDebounceEvent(async () => {
      prepareMapDataRequest();
    });
  }, [addDebounceEvent, prepareMapDataRequest]);

  const isLoading = isDebouncing
    || deviceStatsRequest.loading
    || mapDataRequest.loading
    || initialBoundsRequest.loading;

  const { smoothZoomIn, smoothZoomOut, zoomInDisabled, zoomOutDisabled } = useZoom(mapRef, 1);

  return <React.Fragment>
    <div className="cluster-map shared-map" data-id="cluster-map">
      {/* cannot be with other leaflet controls: https://github.com/LiveBy/react-leaflet-control/issues/16 */}
      <div className="mapZoomButtonContainer">
        <i
          className="mapZoomButton fas fa-plus"
          onPointerDown={!zoomInDisabled ? smoothZoomIn.pointerDown : undefined}
          onPointerUp={!zoomInDisabled ? smoothZoomIn.pointerUp : undefined}
          aria-disabled={zoomInDisabled}
          aria-label="Zoom in"
          title="Zoom in"
        />
        <i
          className="mapZoomButton fas fa-minus"
          onPointerDown={!zoomOutDisabled ? smoothZoomOut.pointerDown : undefined}
          onPointerUp={!zoomOutDisabled ? smoothZoomOut.pointerUp : undefined}
          aria-disabled={zoomOutDisabled}
          aria-label="Zoom out"
          title="Zoom out"
        />
      </div>
      <NoSelectionOverlay
        show={!isLoading && (initialBoundsRequest.data === null || !deviceStatsRequest.data?.withLocationCount)}
        noSelectionText={t('NO_DATA_AVAILABLE')}
      />
      <Map
        ref={ref => { if (ref) mapRef.current = ref; }}
        zoomControl={false}
        bounds={[[-90, -180], [90, 180]]}
        onmoveend={handleChange}
        onzoomend={() => {
          if (state?.selectedCluster) {
            dispatch({ type: 'SET_SELECTED_CLUSTER', payload: null });
          }
          handleChange();
        }}
        onresize={resetInitialBounds}
        maxZoom={18}
        zoomSnap={1}
      >
        <Control position="topright">
          <i className="mapResetButton fas fa-undo-alt" aria-label="Reset map" onClick={resetInitialBounds} />
        </Control>
        <Control position="bottomleft">
          <div className="bottom-left-container">
            {isLoading && <div className='loading-indicator'>
              <i className="fas fa-spinner fa-spin" />
              {t('LOADING')}
            </div>}
            {deviceStatsRequest.data && (deviceStatsRequest.data.totalCount > deviceStatsRequest.data.withLocationCount) && <div className="device-stats" data-id="device-stats">
              <Trans ns='maps' i18nKey={'LOCATION_COLLECTION_INFO'} values={deviceStatsRequest.data}><strong>0</strong> 1 <strong>2</strong> 3</Trans>
            </div>}
          </div>
        </Control>
        <OverlayLayer mapRef={mapRef} data={mapDataRequest.data} isLoading={isLoading} />
        {mapTilesShown && <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution="&copy; <a href=&quot;http://osm.org/copyright&quot;>OpenStreetMap</a> contributors"
          noWrap
          minZoom={0}
          maxZoom={20}
        />}
      </Map>
    </div>
  </React.Fragment>;
};
